/**
 * 曲谱名的单一事实来源：读它、写它、以及"还没有名字"时怎么办。
 *
 * 库里存的 `title` 与谱面印的 `Dt:` 必须是**同一个字符串**。这一条以前散在
 * 两处各写一半（保存流程 `TestApp.saveToCloud` 写、列表页改名 `LibraryPage`
 * 只改库不动谱面），于是出现「库里叫《我的歌》、谱面还印《小星星》」的错位。
 *
 * 现在两个动作都从这里取函数：改名字的地方一律 `withDocTitle()` 顺手把谱面
 * 也改掉，读名字的地方一律 `extractTitle()`。
 */

/**
 * 还没有名字的曲谱在库里显示成这个。
 *
 * 库里每一行必须有名字（`title` 非空），而新谱在建行那一刻确实还没名字，
 * 所以先占这个值；第一次保存时 `TestApp.saveToCloud` 见谱面无曲名会弹取名框，
 * 把它换成真名。
 */
export const UNTITLED_SCORE_TITLE = '未命名曲谱'

/**
 * 读谱面里的曲名，没有就返回 `undefined`。
 *
 * ⚠️ 只认**行首**的 `Dt:`（`^Dt:`，多行模式）。缩进的、写在行中间的都不算：
 *    那些位置在解析器里不是命令，取出来会得到一个谱面上并不存在的名字。
 */
export function extractTitle(content: string): string | undefined {
	const m = content.match(/^Dt:\s*(.+)$/m)
	return m ? m[1].trim() : undefined
}

/**
 * 把曲谱名写进源码的 `Dt:` 行。
 *
 * 没有 `Dt:` 行时插在开头（跳过 `//` 注释行，注释是给人读的，别被标题劈开）。
 * 换行必须压掉：一行一个命令，标题里混进换行会直接改变文档结构。
 */
export function withDocTitle(content: string, title: string): string {
	const clean = title.replace(/[\r\n]+/g, ' ').trim()
	if (!clean) return content
	const lines = content.split('\n')
	const at = lines.findIndex(line => /^\s*Dt\s*:/.test(line))
	if (at >= 0) {
		if (lines[at].trim() == 'Dt: ' + clean) return content
		lines[at] = 'Dt: ' + clean
		return lines.join('\n')
	}
	let insertAt = 0
	while (insertAt < lines.length && /^\s*\/\//.test(lines[insertAt])) insertAt += 1
	lines.splice(insertAt, 0, 'Dt: ' + clean)
	return lines.join('\n')
}

/**
 * 这个曲名是不是"还没有名字"的占位值。
 *
 * 新建的谱先以 `UNTITLED_SCORE_TITLE` 建行（有 id 才能刷新恢复），所以
 * 保存流程读到这个名字时**不能当成真名**：否则用户点保存，库名原样写回，
 * 取名框永远不会弹。
 */
export function isUntitled(title: string | null | undefined): boolean {
	return !title || !title.trim() || title.trim() === UNTITLED_SCORE_TITLE
}

/** 曲名是从哪来的 —— 保存流程据此决定要不要把名字补进谱面。 */
export type TitleSource = 'doc' | 'path' | 'placeholder'

/**
 * 保存时该用哪个名字：谱面 `Dt:` → 文件名/库名 → 占位名。
 *
 * ⚠️ **谱面优先**，这条顺序是 2026-10-28 反过来的，别改回去。
 *
 * 原来文件名在前，理由是"打开哪个文件就叫那个名"。但谱面的 `Dt:` 是作者
 * 亲手敲的字，文件名只是"这份内容从哪来的"—— 从「＋ 新建曲谱」进来（文件名
 * 是占位名「未命名曲谱」）再导入一份带 `Dt:` 的谱，或者打开谱后直接改 `Dt:`，
 * 旧顺序都会把用户刚写的名字丢掉：库里存的还是导入时那个标题（用户实测报的
 * 就是这个：改成 `Dt: love story`，保存后库里没变）。
 *
 * 反向不成立的地方也验证过：库里改名走 `withDocTitle()`，库名与谱面同时改，
 * 不会出现"谱面旧名把库名抢回去"。
 */
export function resolveScoreTitle(input: {
	content: string
	path?: string
	/** 都没有时用什么（默认占位名） */
	fallback?: string
}): {title: string, source: TitleSource} {
	const docTitle = extractTitle(input.content)
	if (docTitle) return {title: docTitle, source: 'doc'}
	const path = typeof input.path === 'string' ? input.path.trim() : ''
	if (path && path !== UNTITLED_SCORE_TITLE) return {title: path, source: 'path'}
	return {title: input.fallback ?? UNTITLED_SCORE_TITLE, source: 'placeholder'}
}
